import { z } from "zod";
import { staffRoleSchema } from "./enums";

/**
 * Staff PIN and quick switch on a shared device (OP-19, P6-T11): each
 * member sets a 4 digit PIN; on a device signed in as one person, another
 * member picks their name and types their PIN, and what they do is recorded
 * under them. See docs/architecture/auth-tenancy.md, Staff PIN.
 */

/** PINs anyone would try first are refused: one digit four times, and 1234. */
export function isWeakPin(pin: string): boolean {
	return /^(\d)\1{3}$/.test(pin) || pin === "1234";
}

export const pinSchema = z
	.string()
	.regex(/^\d{4}$/, "Four digits")
	.refine((pin) => !isWeakPin(pin), "Too easy to guess");

/** `staff.tenancy.setMyPin`: the caller's own PIN in this restaurant. */
export const setPinInputSchema = z.object({ pin: pinSchema });

/** `staff.tenancy.getMyPin`. */
export const myPinSchema = z.object({ hasPin: z.boolean() });
export type MyPin = z.infer<typeof myPinSchema>;

/**
 * A person this device can switch to: an active member with a PIN, or the
 * person signed in on the device (`isSignedIn`), who may have none.
 */
export const switchablePersonSchema = z.object({
	userId: z.uuid(),
	name: z.string().nullable(),
	email: z.string().nullable(),
	avatarUrl: z.string().nullable(),
	role: staffRoleSchema,
	hasPin: z.boolean(),
	isSignedIn: z.boolean(),
});
export type SwitchablePerson = z.infer<typeof switchablePersonSchema>;

/** `staff.tenancy.switchPerson`. Switching to the signed-in person ends the switch. */
export const switchPersonInputSchema = z.object({
	userId: z.uuid(),
	/** Needed unless switching back to a signed-in person who has no PIN. */
	pin: z
		.string()
		.regex(/^\d{4}$/)
		.optional(),
});
export type SwitchPersonInput = z.infer<typeof switchPersonInputSchema>;

/**
 * The switch: `token` goes with every call as `x-staff-switch` until it
 * expires (12 hours, a long shift) or someone switches again; null when
 * the device is back to the signed-in person.
 */
export const staffSwitchSchema = z.object({
	token: z.string().nullable(),
	expiresAt: z.iso.datetime().nullable(),
	userId: z.uuid(),
	name: z.string().nullable(),
	role: staffRoleSchema,
});
export type StaffSwitch = z.infer<typeof staffSwitchSchema>;

/** Hours a switch lasts. */
export const STAFF_SWITCH_HOURS = 12;
